import { Button } from 'primereact/button';
import { useState, useRef, useEffect } from 'react';
import { CustomFileUpload } from './CustomFileUpload';
import { Toast } from 'primereact/toast';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';

export const PdfImageExtractorForm = () => {
    const { getAccessTokenSilently } = useAuth0();
    useEffect(() => { document.title = 'OptiConfig PDF Image Extractor'; }, []);

    const toast = useRef(null);

    const showError = (err) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: err,
                life: 3000
            });
        }
    }

    const showSuccess = (msg) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: msg,
                life: 3000
            });
        }
    };

    const [token, setToken] = useState("");
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [sessionId, setSessionId] = useState(crypto.randomUUID());
    const [succeeded, setSucceeded] = useState(false);
    const [startText, setStartText] = useState("");
    const [endText, setEndText] = useState("");
    const [minSize, setMinSize] = useState(null);

    useEffect(() => {
        const fetchToken = async () => {
            try {
                const accessToken = await fetchAccessToken(getAccessTokenSilently)
                setToken(accessToken);
            } catch (error) {
                console.error('Error fetching access token:', error);
                showError('Failed to fetch access token. Please try again.');
            }
        };
        fetchToken();
    }, [getAccessTokenSilently]);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!uploadedFiles || uploadedFiles.length === 0) {
            showError('Please upload at least one PDF file.');
            return;
        }

        setSubmitting(true);

        const fileNames = uploadedFiles.map(file => file.name);

        const payload = {
            fileNames,
            startText,
            endText,
            minSize
        };

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/pdf/extract-images/${sessionId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) await apiCallErrorHandler(response, 'Failed to submit. Please try again.');

            setSucceeded(true);

            showSuccess('Files processed successfully');
        } catch (error) {
            showError(error.message || 'Submission failed.');
        } finally {
            setSubmitting(false);
        }
    }

    const discardSession = async (e) => {
        e?.preventDefault();
        try {
            await fetch(`${process.env.REACT_APP_API_URL || ''}/file/discard/${sessionId}` , {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });
        } catch (err) {
            showError(err.message || 'Failed to discard session on server.');
        } finally {
            setUploadedFiles([]);
            setSucceeded(false);
            setSessionId(crypto.randomUUID());
        }
    }

    const onDownload = async (e) => {
        e.preventDefault();

        setSubmitting(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/file/download/${sessionId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) await apiCallErrorHandler(response, 'Failed to download. Please try again.');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${sessionId}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            showSuccess('File downloaded successfully');
            
            await discardSession();
        } catch (error) {
            showError(error.message || 'Download failed.');
        } finally {
            setSubmitting(false);
        }

    };

    return (
        <form className="grid" style={{ maxWidth: '100vw', overflowX: 'auto' }} onSubmit={onSubmit}>
            <Toast ref={toast} />
            <div className="col-12">
                <div className="grid formgrid p-fluid">
                    <div className="field col-12">
                        <h4 style={{ textAlign: 'center' }}>PDF Image Extractor</h4>
                    </div>
                    <div className="field col-4"></div>
                    <div className="field col-4" style={{ display: 'flex', justifyContent: 'center' }}>
                        {!succeeded && <Button
                            type="submit"
                            label={submitting ? "Submitting..." : "Submit"}
                            className="p-button-primary"
                            style={{ maxWidth: '300px', width: '100%' }}
                            disabled={uploadedFiles.length === 0 || submitting}
                        />}
                        {succeeded && <>
                            <Button
                                onClick={onDownload}
                                label={submitting ? "Downloading..." : "Download .zip"}
                                className="p-button-primary"
                                style={{ width: '50%' }}
                                disabled={uploadedFiles.length === 0 || submitting}
                            />
                        </>}
                        {uploadedFiles.length > 0 && (
                            <Button
                                onClick={discardSession}
                                label={"Discard"}
                                className="p-button-secondary"
                                style={{ width: '50%', marginLeft: 20 }}
                                disabled={submitting}
                            />
                        )}
                    </div>
                    <div className="field col-4">
                        {submitting && <ProgressSpinner style={{ width: 30, height: 30 }} strokeWidth="4" />}
                    </div>
                    <div className="field col-3">
                    </div>
                    <div className="field col-6" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                        <CustomFileUpload
                            acceptedFileTypes={['application/pdf', '.pdf']}
                            onMessage={(msg) => { showError(msg.detail); }}
                            token={token}
                            fileValue={uploadedFiles}
                            sessionId={sessionId}
                            onChangeFiles={(files) => {
                                setSucceeded(false);
                                setUploadedFiles(files);
                            }}
                        />
                    </div>
                    <div className="field col-3"></div>
                    <div className="field col-3"></div>
                    <div className="field col-3">
                        <InputText
                            value={startText}
                            onChange={e => setStartText(e.target.value)}
                            placeholder="Start from Text"
                            className="w-full"
                        />
                    </div>
                    <div className="field col-3">
                        <InputText
                            value={endText}
                            onChange={e => setEndText(e.target.value)}
                            placeholder="End with Text"
                            className="w-full"
                        />
                    </div>
                    <div className="field col-3"></div>
                    <div className="field col-3"></div>
                    <div className="field col-3">
                        <InputNumber
                            value={minSize}
                            onValueChange={e => setMinSize(e.value)}
                            min={1}
                            max={10000}
                            placeholder="Min image size, KB (50 by default)"
                            className="w-full"
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};